import {
  autoUpdate,
  flip,
  offset,
  shift,
  useFloating,
} from '@floating-ui/vue'
import { onClickOutside } from '@vueuse/core'

import type { CSSProperties, ComputedRef, Ref } from 'vue'
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import type { Order } from './interfaces'

import { OrderStatus } from '@brighterly/lib-core-types/orders/enums'

import { faEllipsisVertical } from '@fortawesome/pro-regular-svg-icons'

import { useSidebarPrivileges } from '@entities/viewer'
import { useOrderSync } from '@features/order-actions'
import { getCustomerUrl, getOrderInvoiceUrl, getOrderUrl } from '@shared/lib/routes'

import { useOrdersApi } from '../api/list.hook'

interface UseOrderActionsOptions {
  order: ComputedRef<Order>
}

interface OrderActionsReturn {
  isOpen: Ref<boolean>
  containerRef: Ref<HTMLElement | null>
  floatingRef: Ref<HTMLElement | null>
  floatingStyles: Ref<CSSProperties>
  triggerIcon: IconDefinition
  hasCustomer: ComputedRef<boolean>
  canManage: ComputedRef<boolean>
  canCustomiseInvoice: ComputedRef<boolean>
  toggle: () => void
  openOrder: () => Promise<void>
  openCustomer: () => Promise<void>
  openInvoice: () => Promise<void>
  syncOrder: () => Promise<void>
}

export const useOrderActions = ({ order }: UseOrderActionsOptions): OrderActionsReturn => {
  const { canManageOrders } = useSidebarPrivileges()

  const isOpen = ref(false)
  const containerRef = ref<HTMLElement | null>(null)
  const floatingRef = ref<HTMLElement | null>(null)

  const { floatingStyles } = useFloating(containerRef, floatingRef, {
    placement: 'bottom-end',
    strategy: 'fixed',
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(4),
      flip(),
      shift({ padding: 8 }),
    ],
  })

  const hasCustomer = computed<boolean>(() => !!order.value.customer)
  const canManage = computed<boolean>(() => canManageOrders.value)
  const canCustomiseInvoice = computed<boolean>(() => order.value.status === OrderStatus.SUCCEEDED)

  const toggle = (): void => {
    isOpen.value = !isOpen.value
  }

  const openOrder = async (): Promise<void> => {
    isOpen.value = false
    await navigateTo(getOrderUrl(order.value.id))
  }

  const openCustomer = async (): Promise<void> => {
    const customer = order.value.customer
    if (!customer) return

    isOpen.value = false
    await navigateTo(getCustomerUrl(customer.id))
  }

  const openInvoice = async (): Promise<void> => {
    isOpen.value = false
    await navigateTo(getOrderInvoiceUrl(order.value.id))
  }

  const syncOrder = async (): Promise<void> => {
    isOpen.value = false

    const synced = await useOrderSync(order.value.id)

    if (synced) {
      await useOrdersApi()
    }
  }

  onClickOutside(containerRef, () => {
    isOpen.value = false
  })

  return {
    isOpen,
    containerRef,
    floatingRef,
    floatingStyles,
    triggerIcon: faEllipsisVertical,
    hasCustomer,
    canManage,
    canCustomiseInvoice,
    toggle,
    openOrder,
    openCustomer,
    openInvoice,
    syncOrder,
  }
}
